import type { NavItem } from './venueNav';

type SwitchTargetParams = {
  /** The current pathname, e.g. `/loc/<cur>/shifts`. */
  pathname: string;
  /** The venue currently in scope (the `:venueId` path param). */
  currentVenueId: string;
  /** The venue being switched to. */
  targetVenueId: string;
  /** Venue-bound nav, used to resolve a sub-path's permissions. */
  navItems: NavItem[];
  /** Whether the user holds any of the given permission codes at the *target* venue. */
  canAtTarget: (permissions: string[]) => boolean;
};

/**
 * Where a Venue switch should land (ADR 0015, PRD #72): keep the user on the same feature
 * sub-path at the new Venue when they are permitted to use it there, otherwise fall back to
 * the new Venue's home. An always-visible feature (no permissions) and the home itself
 * always survive; an unknown sub-path falls back to home. Only the first segment after the
 * venue id is treated as the feature — deep detail paths collapse to the feature root,
 * since a detail at one Venue has no meaning at another. Pure so every branch is unit
 * testable without a router.
 */
export function computeSwitchTarget({
  pathname,
  currentVenueId,
  targetVenueId,
  navItems,
  canAtTarget,
}: SwitchTargetParams): string {
  const home = `/loc/${targetVenueId}`;
  const subPath = featureSubPath(pathname, currentVenueId);
  if (!subPath) return home;

  const item = navItems.find((navItem) => navItem.path === subPath);
  if (!item) return home;
  if (item.permissions && !canAtTarget(item.permissions)) return home;
  return `${home}/${subPath}`;
}

/** The first path segment after `/loc/<currentVenueId>/`, or '' at the venue home. */
function featureSubPath(pathname: string, currentVenueId: string): string {
  const prefix = `/loc/${currentVenueId}`;
  if (!pathname.startsWith(prefix)) return '';
  const rest = pathname.slice(prefix.length).replace(/^\/+/, '');
  return rest.split('/')[0] ?? '';
}
